import { clearLocalUserData, setLocalUserData } from "@/utils";
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

interface UserData {
  access_token: string;
  expires_in: number;
  refresh_token: string;
  user: {
    id: string;
    full_name: string;
    email: string;
    phone: string | null;
    type: string;
    bc_verified: boolean;
    photo: string;
    photo_thumbnail_url: string;
    permissions: string[];
    login_provider: string;
    token_type: string;
    organization_id: string;
    organization_owner: boolean;
    email_verified: boolean;
    onboarding_step: number;
    organization: {
      id: string;
      name: string;
      tax_id: string;
      logo: string;
      status: string;
    };
  };
  subscription: {
    subscription_id: string;
    customer_id: string;
    plan_name: string;
    product_id: string;
    price_id: string;
    starts_at_ms: number;
    expiration_at_ms: number;
    features: { shortlist: number; job_post: number };
    is_trialing: boolean;
    have_trial: boolean;
    is_cancelled: boolean;
  };
}

interface AuthState {
  userData: UserData | null;
}

const initialState: AuthState = {
  userData: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    login: (state, action: PayloadAction<UserData>) => {
      setLocalUserData(action.payload);
      state.userData = action.payload;
    },
    logout: (state) => {
      clearLocalUserData();
      state.userData = null;
    },
    refreshToken: (state, action: PayloadAction<string>) => {
      if (state.userData) {
        const updatedUserData = { ...state.userData, access_token: action.payload };
        setLocalUserData(updatedUserData);
        state.userData = updatedUserData;
      }
    },
    updateFullName: (state, action: PayloadAction<string>) => {
      const updatedUserData = {
        ...state.userData,
        user: { ...state.userData.user, full_name: action.payload },
      };
      setLocalUserData(updatedUserData);
      state.userData.user.full_name = action.payload;
    },
    updatePhoneNumber: (state, action: PayloadAction<string>) => {
      const updatedUserData = {
        ...state.userData,
        user: { ...state.userData.user, phone: action.payload },
      };
      setLocalUserData(updatedUserData);
      state.userData.user.phone = action.payload;
    },
    updatePhoto: (state, action: PayloadAction<{ photo: string; photo_thumbnail_url: string }>) => {
      const updatedUserData = {
        ...state.userData,
        user: {
          ...state.userData.user,
          photo: action.payload.photo,
          photo_thumbnail_url: action.payload.photo_thumbnail_url,
        },
      };
      setLocalUserData(updatedUserData);
      state.userData.user.photo = action.payload.photo;
      state.userData.user.photo_thumbnail_url = action.payload.photo_thumbnail_url;
    },
    updateLogo: (state, action: PayloadAction<{ logo: string }>) => {
      const updatedUserData = {
        ...state.userData,
        user: {
          ...state.userData.user,
          organization: {
            ...state.userData.user.organization,
            logo: action.payload.logo,
          },
        },
      };
      setLocalUserData(updatedUserData);
      state.userData.user.organization.logo = action.payload.logo;
    },
  },
});

export const {
  login,
  logout,
  refreshToken,
  updateFullName,
  updatePhoneNumber,
  updatePhoto,
  updateLogo,
} = authSlice.actions;
export default authSlice.reducer;
